import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import styled from 'styled-components';
import { Redirect } from 'react-router-dom';
import { useLastLocation } from 'react-router-last-location';
import Paragraph from 'components/atoms/Paragraph';
import LoginKey from 'components/molecules/LoginKey';
import { database } from '../helpers/firebase';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  max-height: 100vh;
  max-width: 100vw;
  padding: 25px;
`;

const Form = styled.form`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
`;

const Input = styled.input`
  background-color: ${({ theme }) => theme.gray4};
  border: none;
  border-radius: 8px;
  outline: none;
  padding: 12px;
  width: 300px;
  color: ${({ theme }) => theme.text};
  font-size: ${({ theme }) => theme.heading};
  font-family: 'Poppins', sans-serif;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.16);

  ::placeholder {
    color: ${({ theme }) => theme.textDisabled};
  }
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.gray4};
  border: none;
  border-radius: 8px;
  outline: none;
  padding: 12px;
  margin-left: 15px;
  color: ${({ theme }) => theme.text};
  font-size: ${({ theme }) => theme.heading};
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transform: ${({ isShown }) => (isShown ? 'translateX(0)' : 'translateX(-30px)')};
  opacity: ${({ isShown }) => (isShown ? '1' : '0')};
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;

  :hover {
    background-color: ${({ theme }) => theme.gray3};
  }
`;

const Progress = styled.div`
  position: relative;
`;

const Bar = styled(Paragraph)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: hidden;
  color: transparent;
  user-select: none;
  text-align: center;
  line-height: 58px;
  border-radius: 8px;
  font-size: ${({ theme }) => theme.heading};
  background-color: ${({ theme, color }) => theme[color]};
`;

const Console = styled.div`
  position: absolute;
  top: calc(100% + 30px);
  left: 0;
  display: flex;
  flex-flow: column nowrap;
  width: 300px;
  height: 160px;
  border-radius: 10px;
  background-color: black;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  visibility: hidden;
`;

const ConsoleTopBar = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 0 5px 0 10px;
  height: 24px;
  border-bottom: 1px solid silver;
`;

const Controls = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;

const Control = styled.div`
  height: 13px;
  width: 13px;
  border-radius: 50%;
  background-color: ${({ theme, color }) => theme[color]};
  margin-right: 5px;
`;

const ConsoleContent = styled.pre`
  padding: 10px;
  font-size: 13px;
`;

const StyledLoginKey = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
`;

function Login({ basicPassword, isLogged, setIsLogged, setIsAdmin, setIsAuthorized }) {
  const passRef = useRef(null);
  const barRef = useRef(null);
  const consoleRef = useRef(null);

  const [password, setPassword] = useState('');
  const [wrongPasswordCounter, setWrongPasswordCounter] = useState(1);
  const [userType, setUserType] = useState('GOŚĆ');
  const [userPermissions, setUserPermissions] = useState('PODSTAWOWE');
  const [isSecretKeyPressed, setIsSecretKeyPressed] = useState(true);
  const [barColor, setBarColor] = useState('green');

  const lastLocation = useLastLocation();
  
  const authAnimation = (auth, adm) => {
    setPassword('');
    const tl = gsap.timeline();

    if (passRef.current) passRef.current.readOnly = true;

    let color = 'rgb(48, 209, 88)';

    if (auth) color = 'rgb(255, 159, 10)';
    if (adm) color = 'rgb(255, 69, 58)';

    tl.to(passRef.current, { color: 'rgb(58, 58, 60)' })
      .fromTo(
        consoleRef.current,
        { autoAlpha: 0, y: 30 },
        { autoAlpha: 1, y: 0, duration: 0.6 },
        0.25,
      )
      .set(barRef.current, { color })
      .to(barRef.current, { color: 'rgb(255, 255, 255, .87)' })
      .to(consoleRef.current, { autoAlpha: 0, y: 30 }, '-=.4')
      .fromTo(
        barRef.current,
        { autoAlpha: 1, width: 0 },
        { width: '100%', duration: tl.duration() },
        0,
      )
      .call(
        () => {
          if (auth) setIsAuthorized(true);
          if (adm) setIsAdmin(true);
          setIsLogged(true);
        },
        null,
        '+=.3',
      );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (password === basicPassword) {
      setBarColor('green');
      setUserType('GOŚĆ');
      setUserPermissions('PODSTAWOWE');
      authAnimation();
    } else if (password === '*') {
      setPassword('');
      setIsSecretKeyPressed((prevState) => !prevState);
    } else if (password === process.env.REACT_APP_ADMIN_SHORT_PASSWORD && isSecretKeyPressed) {
      setBarColor('red');
      setUserType('ADMIN');
      setUserPermissions('WSZYSTKIE');
      authAnimation(false, true);
    } else if (password === process.env.REACT_APP_ADMIN_PASSWORD) {
      setBarColor('red');
      setUserType('ADMIN');
      setUserPermissions('WSZYSTKIE');
      authAnimation(false, true);
    } else if (password === process.env.REACT_APP_PASSWORD_AUTH) {
      setBarColor('yellow');
      setUserType('ZWERYFIKOWANY');
      setUserPermissions('PODSTAWOWE');
      authAnimation(true);
    } else if (password === process.env.REACT_APP_FORBIDDEN) {
      const tl = gsap.timeline({ defaults: { ease: 'linear', duration: 0.05 } });

      tl.to(passRef.current, { backgroundColor: 'rgb(255, 69, 58)' });

      setInterval(() => {
        tl.to(passRef.current, { x: `${Math.floor(Math.random() * 20) + 20}px` })
          .to(passRef.current, {
            x: `-${Math.floor(Math.random() * 20) + 10}px`,
            y: `${Math.floor(Math.random() * 20) + 10}px`,
          })
          .to(passRef.current, { x: `-${Math.floor(Math.random() * 20) + 10}px` })
          .to(passRef.current, {
            x: `${Math.floor(Math.random() * 20) + 10}px`,
            y: `-${Math.floor(Math.random() * 20) + 10}px`,
          })
          .to(passRef.current, {
            x: `-${Math.floor(Math.random() * 20) + 10}px`,
            y: `${Math.floor(Math.random() * 20) + 10}px`,
          })
          .to(passRef.current, {
            x: `${Math.floor(Math.random() * 20) + 10}px`,
            y: `-${Math.floor(Math.random() * 20) + 10}px`,
          });
      }, 50);
    } else {
      setWrongPasswordCounter((prevState) => prevState + 1);
      const tl = gsap.timeline({ defaults: { ease: 'linear', duration: 0.1 } });

      if (wrongPasswordCounter <= 5) {
        tl.to(passRef.current, { x: `15px`, backgroundColor: 'rgb(255,69,58)' })
          .to(passRef.current, { x: `-10px` })
          .to(passRef.current, { x: `5px` })
          .to(passRef.current, { x: 0 })
          .to(passRef.current, { backgroundColor: 'rgb(58, 58, 60)' });
      } else {
        tl.to(passRef.current, {
          x: `${Math.floor(Math.random() * 10) + 10}px`,
          backgroundColor: 'rgb(255, 69, 58)',
        })
          .to(passRef.current, {
            x: `${Math.floor(Math.random() * 10) + 5}px`,
            y: `${Math.floor(Math.random() * 10) + 5}px`,
          })
          .to(passRef.current, { x: `-${Math.floor(Math.random() * 10) + 5}px` })
          .to(passRef.current, {
            x: `-${Math.floor(Math.random() * 10) + 5}px`,
            y: `-${Math.floor(Math.random() * 10) + 5}px`,
          })
          .to(passRef.current, {
            x: `-${Math.floor(Math.random() * 10) + 5}px`,
            y: `${Math.floor(Math.random() * 10) + 5}px`,
          })
          .to(passRef.current, {
            x: `${Math.floor(Math.random() * 10) + 5}px`,
            y: `-${Math.floor(Math.random() * 10) + 5}px`,
          })
          .to(passRef.current, { x: 0, y: 0 })
          .to(passRef.current, { backgroundColor: 'rgb(58, 58, 60)' });
      }
    }
  };

  if (isLogged)
    return <Redirect to={lastLocation.pathname === '/login' ? '/' : lastLocation.pathname} />;

  return (
    <Wrapper>
      <StyledLoginKey>
        <LoginKey
          setUserType={setUserType}
          setUserPermissions={setUserPermissions}
          authAnimation={authAnimation}
          setBarColor={setBarColor}
        />
      </StyledLoginKey>
      <Form onSubmit={handleSubmit}>
        <Progress>
          <Input
            type={isSecretKeyPressed ? 'password' : 'text'}
            ref={passRef}
            autoFocus
            placeholder="Podaj hasło"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={!basicPassword.length}
          />
          <Bar ref={barRef} color={barColor}>
            Hasło poprawne
          </Bar>
        </Progress>
        <Button type="submit" isShown={password.length}>
          Wejdź
        </Button>
        <Console ref={consoleRef}>
          <ConsoleTopBar>
            <div />
            <Controls>
              <Control color="yellow" />
              <Control color="green" />
              <Control color="red" />
            </Controls>
          </ConsoleTopBar>
          <ConsoleContent>
            &gt; WERYFIKACJA [▉▉▉▉▉▉▉▉▉▉▉▉▉]
            <br />
            &gt; WERYFIKACJA POMYŚLNA
            <br />
            &gt; UŻYTKOWNIK: {userType}
            <br />
            &gt; UPRAWNIENIA: {userPermissions}
          </ConsoleContent>
        </Console>
      </Form>
    </Wrapper>
  );
}

Login.propTypes = {
  basicPassword: PropTypes.string.isRequired,
  setIsLogged: PropTypes.func.isRequired,
  setIsAdmin: PropTypes.func.isRequired,
  setIsAuthorized: PropTypes.func.isRequired,
  isLogged: PropTypes.bool,
};

Login.defaultProps = {
  isLogged: false,
};

export default Login;
